import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Mail, Phone, MapPin, Send, ArrowLeft, Clock, CheckCircle2 } from 'lucide-react';
import { submitContactForm } from '@/services/api';

const LOGO_URL = "/admixio-logo.png";

const ContactPage = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      return toast.error('Please fill in Name, Email, and Message');
    }
    setLoading(true);
    try {
      await submitContactForm(form);
      setSubmitted(true);
      toast.success('Your query has been submitted!');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Nav */}
      <nav className="bg-slate-900 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Admixio" className="h-14" data-testid="logo" />
          </Link>
          <Link to="/login">
            <button className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6 py-2.5 text-sm font-medium transition-colors" data-testid="open-portal-btn">
              Open Portal
            </button>
          </Link>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-6 py-16">
        {/* Back link */}
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>

        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Contact <span className="text-blue-600">Us</span>
          </h1>
          <p className="text-slate-600 text-lg max-w-xl mx-auto">
            Have a question or want to partner with us? We'd love to hear from you.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Contact Info */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="bg-white border-slate-200 shadow-sm rounded-2xl">
              <CardContent className="p-6 space-y-6">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Get in Touch
                  </h2>
                  <p className="text-slate-500 text-sm leading-relaxed">
                    Reach out to us for partnership inquiries, support, or any questions about our platform.
                  </p>
                </div>

                <div className="space-y-4">
                  <a href="mailto:info@admixio.com" className="flex items-center gap-4 p-3 rounded-xl bg-blue-50 border border-blue-100 hover:bg-blue-100 transition-colors group" data-testid="email-link">
                    <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Email Us</p>
                      <p className="text-sm font-medium text-slate-900 group-hover:text-blue-600 transition-colors">info@admixio.com</p>
                    </div>
                  </a>

                  <a href="tel:+16479386338" className="flex items-center gap-4 p-3 rounded-xl bg-blue-50 border border-blue-100 hover:bg-blue-100 transition-colors group" data-testid="phone-link">
                    <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Call Us</p>
                      <p className="text-sm font-medium text-slate-900 group-hover:text-blue-600 transition-colors">647-938-6338</p>
                    </div>
                  </a>

                  <div className="flex items-center gap-4 p-3 rounded-xl bg-slate-50 border border-slate-200">
                    <div className="w-10 h-10 rounded-lg bg-slate-700 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Location</p>
                      <p className="text-sm font-medium text-slate-900">Toronto, Ontario, Canada</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 p-3 rounded-xl bg-slate-50 border border-slate-200">
                    <div className="w-10 h-10 rounded-lg bg-slate-700 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Business Hours</p>
                      <p className="text-sm font-medium text-slate-900">Mon - Fri, 9 AM - 6 PM EST</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-3">
            <Card className="bg-white border-slate-200 shadow-sm rounded-2xl">
              <CardContent className="p-6">
                {submitted ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-green-50 flex items-center justify-center">
                      <CheckCircle2 className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      Query Submitted!
                    </h3>
                    <p className="text-slate-500 mb-6 max-w-sm mx-auto">
                      Thank you for reaching out. Our team will get back to you within 24 hours.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <Button onClick={() => { setSubmitted(false); setForm({ name: '', email: '', phone: '', subject: '', message: '' }); }} variant="outline" className="border-slate-300 text-slate-700 rounded-lg" data-testid="send-another-btn">
                        Send Another Query
                      </Button>
                      <Link to="/">
                        <Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg">
                          Back to Home
                        </Button>
                      </Link>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <h2 className="text-lg font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      Send Us a Message
                    </h2>
                    <p className="text-slate-500 text-sm mb-4">Fields marked with * are required</p>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <label className="text-sm text-slate-700 font-medium">Full Name *</label>
                        <Input
                          value={form.name}
                          onChange={e => handleChange('name', e.target.value)}
                          placeholder="John Doe"
                          className="bg-white border-slate-300 text-slate-900 focus:border-blue-500 h-11 rounded-lg"
                          data-testid="contact-name"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-sm text-slate-700 font-medium">Email Address *</label>
                        <Input
                          type="email"
                          value={form.email}
                          onChange={e => handleChange('email', e.target.value)}
                          placeholder="you@company.com"
                          className="bg-white border-slate-300 text-slate-900 focus:border-blue-500 h-11 rounded-lg"
                          data-testid="contact-email"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <label className="text-sm text-slate-700 font-medium">Phone Number</label>
                        <Input
                          type="tel"
                          value={form.phone}
                          onChange={e => handleChange('phone', e.target.value)}
                          placeholder="+1 (555) 000-0000"
                          className="bg-white border-slate-300 text-slate-900 focus:border-blue-500 h-11 rounded-lg"
                          data-testid="contact-phone"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-sm text-slate-700 font-medium">Subject</label>
                        <Input
                          value={form.subject}
                          onChange={e => handleChange('subject', e.target.value)}
                          placeholder="Partnership Inquiry"
                          className="bg-white border-slate-300 text-slate-900 focus:border-blue-500 h-11 rounded-lg"
                          data-testid="contact-subject"
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-sm text-slate-700 font-medium">Message *</label>
                      <textarea
                        value={form.message}
                        onChange={e => handleChange('message', e.target.value)}
                        placeholder="Tell us how we can help you..."
                        rows={5}
                        className="w-full bg-white border border-slate-300 text-slate-900 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 rounded-lg px-3 py-2 text-sm resize-none outline-none transition-colors"
                        data-testid="contact-message"
                      />
                    </div>

                    <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white h-11 rounded-lg font-medium" data-testid="contact-submit-btn">
                      {loading ? 'Sending...' : <><Send className="w-4 h-4 mr-2" /> Submit Query</>}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 px-6 bg-slate-900 border-t border-slate-800 mt-16">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <img src={LOGO_URL} alt="Admixio" className="h-8" />
          <p className="text-xs text-slate-500">© 2026 Admixio. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default ContactPage;
